import React, { useCallback, useEffect, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ShieldQuestion } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  decideSupportSession,
  listPendingApprovals,
  listSupportSessions,
  revokeSupportSession,
  isSessionLive,
  type SupportSession,
} from "@/lib/support/supportSessions";

/**
 * Visas för användaren när supporten begär skrivläge, och så länge en session är aktiv.
 * Ingen support kan ändra något utan att användaren ser detta.
 */
const SupportApprovalBanner: React.FC = () => {
  const [userId, setUserId] = useState<string | null>(null);
  const [pending, setPending] = useState<SupportSession[]>([]);
  const [active, setActive] = useState<SupportSession[]>([]);

  const load = useCallback(async (uid: string) => {
    try {
      const [p, all] = await Promise.all([
        listPendingApprovals(uid),
        listSupportSessions({ targetUserId: uid }),
      ]);
      setPending(p);
      setActive(all.filter((s) => s.mode === "write" && isSessionLive(s)));
    } catch {
      /* tyst – bannern är icke-kritisk */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (cancelled || !data?.user) return;
      setUserId(data.user.id);
      void load(data.user.id);
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const decide = async (session: SupportSession, decision: "approved" | "denied") => {
    if (!userId) return;
    try {
      await decideSupportSession(session.id, decision, userId);
      toast.success(decision === "approved" ? "Supporten kan nu hjälpa dig." : "Förfrågan nekad.");
      void load(userId);
    } catch (error: any) {
      toast.error("Kunde inte spara ditt svar", { description: error?.message });
    }
  };

  const stop = async (session: SupportSession) => {
    if (!userId) return;
    await revokeSupportSession(session.id);
    toast.success("Supportens åtkomst är avslutad.");
    void load(userId);
  };

  if (pending.length === 0 && active.length === 0) return null;

  return (
    <div className="space-y-2 py-3">
      {pending.map((s) => (
        <Alert key={s.id}>
          <ShieldQuestion className="h-4 w-4" />
          <AlertTitle>Supporten vill hjälpa dig i projektet</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>
              {s.reason || "Supporten begär tillfällig behörighet att fylla i åt dig."} Åtkomsten
              gäller i högst 8 timmar och allt som görs loggas.
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => void decide(s, "approved")}>
                Godkänn
              </Button>
              <Button size="sm" variant="outline" onClick={() => void decide(s, "denied")}>
                Neka
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ))}

      {active.map((s) => (
        <Alert key={s.id} variant="destructive">
          <ShieldQuestion className="h-4 w-4" />
          <AlertTitle>Supportläge är aktivt</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span>Supporten kan just nu redigera i ditt projekt.</span>
            <Button size="sm" variant="outline" onClick={() => void stop(s)}>
              Avsluta nu
            </Button>
          </AlertDescription>
        </Alert>
      ))}
    </div>
  );
};

export default SupportApprovalBanner;
